import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { API_URL } from "../../utils/config";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { formatNumero, formatDate } from "../../utils/Utilitats";
import { mapejos } from "../../utils/Mapejos";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";

import DetallsPartidaVersio from "./Detallspartidaversio";
import Seleccionarpressupost from "./Seleccionarpressupost";
import PlantillesExplorador from "./PlantillesExplorador";
import AjudaPressupost from "./AjudaPressupost";
import "../dashboard_v2.css";

function classeNiuFila(level) {
  if (!level) return "";
  return `dv2-row-nest--${((Number(level) - 1) % 4) + 1}`;
}

function formatPreu(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${formatNumero(n)} €`;
}

function idPareVal(v) {
  if (v == null || v === "" || v === "null") return null;
  const n = Number(v);
  return Number.isFinite(n) && n !== 0 ? n : null;
}

function esItemApartat(p) {
  const v = p && typeof p === "object" ? p.EsApartat : p;
  return v === true || v === 1 || v === "1";
}

function dataSegonsEstat(versio, estatVal) {
  if (!versio) return { etiqueta: "Data", valor: "" };
  switch (estatVal || versio.Estat) {
    case "Esborrany":
      return { etiqueta: "Data modificació", valor: versio.DataUltModificacio || "" };
    case "Presentat":
      return { etiqueta: "Data presentació", valor: versio.DataPresentada || "" };
    case "Acceptat":
      return { etiqueta: "Data acceptació", valor: versio.DataAcceptada || "" };
    case "Rebutjat":
      return { etiqueta: "Data rebuig", valor: versio.DataRebutjada || "" };
    default:
      return { etiqueta: "Data", valor: "" };
  }
}

function liniaPreus({ PreuMaObra, PreuMaterials, PreuExtra, PreuCost }) {
  return [
    `Mà d'obra ${formatPreu(PreuMaObra)}`,
    `Materials ${formatPreu(PreuMaterials)}`,
    `Extra ${formatPreu(PreuExtra)}`,
    `Preu cost ${formatPreu(PreuCost)}`,
  ].join(" · ");
}

// ----------------------------------------------
// HELPER: Compara dues partides alfanumèricament (p.ex. 1.2 < 1.10)
// ----------------------------------------------
function compararPartida(aStr, bStr) {
  const aParts = aStr.split(".");
  const bParts = bStr.split(".");
  const len = Math.max(aParts.length, bParts.length);

  for (let i = 0; i < len; i++) {
    const aPart = i < aParts.length ? aParts[i] : "0";
    const bPart = i < bParts.length ? bParts[i] : "0";

    const aIsNumber = !isNaN(aPart);
    const bIsNumber = !isNaN(bPart);
    if (aIsNumber && bIsNumber) {
      const aNum = parseInt(aPart, 10);
      const bNum = parseInt(bPart, 10);
      if (aNum !== bNum) return aNum - bNum;
    } else {
      const comp = aPart.localeCompare(bPart);
      if (comp !== 0) return comp;
    }
  }
  return 0;
}

function versioPerId(list, idVersio) {
  if (!list || list.length === 0 || idVersio == null || idVersio === "") {
    return null;
  }
  return list.find((v) => Number(v.idVersions) === Number(idVersio)) || null;
}

function Versions({ pressupost, onCancel, subnav }) {
  // -- ESTATS PER A LES VERSIONS --
  const [versionsList, setVersionsList] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [vista, setVista] = useState("llista");
  const [modeVersio, setModeVersio] = useState("consultar");
  const [origenPartides, setOrigenPartides] = useState("llista");
  const [estat, setEstat] = useState("");
  const [comentaris, setComentaris] = useState("");
  const [filtreEstatVersio, setFiltreEstatVersio] = useState("");
  const [sortVersions, setSortVersions] = useState({ key: "idVersions", direction: "desc" });
  const [menuAccions, setMenuAccions] = useState(false);
  const [menuTipus, setMenuTipus] = useState(null);

  // -- ESTATS PER PARTIDES & SUBPARTIDES --
  const [activeTab, setActiveTab] = useState("table");
  const [partidesList, setPartidesList] = useState([]);
  
  // Control d'expansió i selecció en un sol lloc:
  const [expandedIds, setExpandedIds] = useState([]);  // IDs de partides/apartats expandits
  const [selectedPartida, setSelectedPartida] = useState(null);

  const [nivellSeleccionat, setNivellSeleccionat] = useState(0);

  const [isCopyingVersion, setIsCopyingVersion] = useState(false);
  const [selectedCopyPressupost, setSelectedCopyPressupost] = useState(pressupost);

  const { empresaSeleccionada, usuariActiu, rolUsuari } = useUserContext();
  const potCrear = tincPermis(rolUsuari, "CREAR");
  const potConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const potModificar = tincPermis(rolUsuari, "MODIFICAR");
  const potEsborrar = tincPermis(rolUsuari, "ESBORRAR");
  const [plantillaPerVersio, setPlantillaPerVersio] = useState(null);
  const [peSel, setPeSel] = useState(null);
  const [peCat, setPeCat] = useState(null);
  const [peExp, setPeExp] = useState({});

  const prevSelectedVersionId = useRef(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [filtrePartida, setFiltrePartida] = useState("");
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [mostraAjuda, setMostraAjuda] = useState(false);
  const [copyPas, setCopyPas] = useState(null);
  const [copyFiltre, setCopyFiltre] = useState("");
  const [isNarrow, setIsNarrow] = useState(false);
  const menuRef = useRef(null);

  // ESC el lliga tornarUnPas més avall, quan ja existeix la navegació per vistes.

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  // ------------------------------------------------
  // Carregar VERSIONS
  // ------------------------------------------------
  useEffect(() => {
    if (!pressupost?.idPressupostos) return;
    fetchVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pressupost]);

  const fetchVersions = async () => {
    try {
      const response = await fetch(`${API_URL}/Versions.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idPressupostos: pressupost.idPressupostos }),
      });
      const data = await response.json();
      if (data.success) {
        const list = data.versions || [];
        setVersionsList(list);
        return { list, idVersioDominant: data.idVersioDominant ?? null };
      } else {
        console.error("Error obtenint versions:", data.message);
        setVersionsList([]);
        return { list: [], idVersioDominant: null };
      }
    } catch (error) {
      console.error("Error carregant versions:", error);
      setVersionsList([]);
      return { list: [], idVersioDominant: null };
    }
  };

  const createVersion = async () => {
    try {
      const response = await fetch(`${API_URL}/Novaversio.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idPressupostos: pressupost.idPressupostos,
        }),
      });
      const data = await response.json();
      if (!data.success) {
        console.error("Error creant versió:", data.message);
        return null;
      }
      const { list } = await fetchVersions();
      if (!list?.length) return null;
      const maxVer = Math.max(...list.map((v) => Number(v.idVersions)));
      return list.find((v) => Number(v.idVersions) === maxVer) || null;
    } catch (error) {
      console.error("Error al crear versió:", error);
      return null;
    }
  };

  const copyVersion = async (idPressupostosDesti) => {
    if (!selectedVersion || !idPressupostosDesti) return;
    try {
      const response = await fetch(`${API_URL}/Copiaversio.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "copy",
          idPressupostos: pressupost.idPressupostos,
          idVersioOriginal: selectedVersion.idVersions,
          idPressupostDesti: idPressupostosDesti,
        }),
      });
      const data = await response.json();
      if (data.success) {
        const destMateix = Number(idPressupostosDesti) === Number(pressupost.idPressupostos);
        alert(
          destMateix
            ? `Còpia creada com a versió ${data.novaVersio} (Esborrany).`
            : `Còpia creada al pressupost destí com a versió ${data.novaVersio} (Esborrany).`
        );
        if (destMateix) {
          const { list } = await fetchVersions();
          const nova = list.find((v) => Number(v.idVersions) === Number(data.novaVersio)) || list[list.length - 1];
          if (nova) {
            prevSelectedVersionId.current = null;
            setSelectedVersion(nova);
            setEstat(nova.Estat || "Esborrany");
            setComentaris(nova.Comentaris || "");
            setVista("llista");
          }
        }
        return true;
      }
      alert(data.message || "Error copiant versió.");
      return false;
    } catch (error) {
      console.error("Error al copiar versió:", error);
      alert("Error de connexió copiant la versió.");
      return false;
    }
  };

  const updateVersion = async (versio = selectedVersion, estatVal = estat, comentarisVal = comentaris) => {
    if (!versio) return false;
    try {
      const response = await fetch(`${API_URL}/Modificarversio.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "update",
          idPressupostos: pressupost.idPressupostos,
          idVersions: versio.idVersions,
          Estat: estatVal,
          Comentaris: comentarisVal,
          usuari: usuariActiu,
        }),
      });
      const data = await response.json();
      if (!data.success) {
        console.error("Error actualitzant versió:", data.message);
        return false;
      }
      await fetchVersions();
      return true;
    } catch (error) {
      console.error("Error al update de versió:", error);
      return false;
    }
  };

  const deleteVersion = async () => {
    if (!selectedVersion) return;
    try {

      const response = await fetch(`${API_URL}/Esborrarversio.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idPressupostos: pressupost.idPressupostos,
          idVersions: selectedVersion.idVersions,
        }),
      });
      const data = await response.json();
      if (data.success) {
        fetchVersions().then(({ list, idVersioDominant }) => {
          if (list.length > 0) {
            const seguent = versioPerId(list, idVersioDominant) || list[list.length - 1];
            if (seguent) {
              setSelectedVersion(seguent);
              setEstat(seguent.Estat || "Esborrany");
              setComentaris(seguent.Comentaris || "Sense comentaris");
            }
          }
        });
      } else {
        console.error("Error esborrant versió:", data.message);
      }
    } catch (error) {
      console.error("Error al delete de versió:", error);
    }
  };

  const refreshVersionsAndKeepSelected = async () => {
    const { list, idVersioDominant } = await fetchVersions();
    if (list.length === 0) {
      setSelectedVersion(null);
      return;
    }
    const stillExists = list.find((v) => v.idVersions === selectedVersion?.idVersions);
    if (stillExists) {
      setSelectedVersion({ ...stillExists });
      setEstat(stillExists.Estat || "Esborrany");
      setComentaris(stillExists.Comentaris || "Sense comentaris");
    } else {
      const seguent = versioPerId(list, idVersioDominant) || list[list.length - 1];
      setSelectedVersion({ ...seguent });
      setEstat(seguent.Estat || "Esborrany");
      setComentaris(seguent.Comentaris || "Sense comentaris");
    }
  };

  const fetchPartides = useCallback (async () => {
    if (!selectedVersion?.idVersions) return;
    try {
      const response = await fetch(`${API_URL}/Partidesversio.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "getPartides",
          idVersions: selectedVersion.idVersions,
          idPressupostos: pressupost.idPressupostos,
        }),
      });
      const data = await response.json();
      if (data.success) {
        const ordenades = (data.partides || []).sort((a, b) =>
          compararPartida(a.Partida, b.Partida)
        );
        setPartidesList(ordenades);
        return ordenades;        // 🔹 AFEGIT: retornem la llista
      } else {
        console.error("Error obtenint partides:", data.message);
        setPartidesList([]);
        return [];               // 🔹 Retornem llista buida
      }
    } catch (error) {
      console.error("Error carregant partides:", error);
      setPartidesList([]);
      return [];                 // 🔹 Retornem llista buida
    }
  }, [selectedVersion, pressupost]);




  // Quan canvia la versió seleccionada, carreguem partides d'aquest pressupost-versió
  useEffect(() => {
    if (vista !== "partides" || !selectedVersion?.idVersions) return;
    fetchPartides();
  }, [vista, selectedVersion, fetchPartides]);


  useEffect(() => {
    if (!selectedPartida) {
      setNivellSeleccionat(0);
    } else {
      let nivell = 0;
      let partidaActual = selectedPartida;
  
      while (partidaActual) {
        const idPare = partidaActual.idPartidaPare;
        if (idPare == null) break; // sortim si no hi ha pare
        const pare = partidesList.find(
          (p) => p.idPartidesPressupost === idPare
        );
        if (!pare) break;
  
        nivell++;
        partidaActual = pare; // avancem al pare
      }
  
      if (expandedIds.includes(selectedPartida.idPartidesPressupost)) {
        nivell++;
      }
  
      setNivellSeleccionat(nivell);
    }
  }, [selectedPartida, expandedIds, partidesList]);

  // 🔹 Quan canvia selecció/ordre/expandits o tornem a la taula → scroll a la fila seleccionada
  useEffect(() => {
    if (vista !== "partides" || activeTab !== "table") return;
    if (selectedRowRef.current) {
      selectedRowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [activeTab, selectedPartida, partidesList, expandedIds, sortConfig]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && activeTab === "usarPlantilla") {
        setActiveTab("table");
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [activeTab]);


  // 🔹 Ref per fer scroll a la fila seleccionada
  const selectedRowRef = useRef(null);

  // 🔹 Saber si una partida és "visible" amb els expandits actuals
  const isPartidaVisible = useCallback((p) => {
    if (!p) return false;
    // nivell 0 sempre visible
    if (p.idPartidaPare == null) return true;
    // tots els ancestres han d’estar a expandedIds
    let parentId = p.idPartidaPare;
    while (parentId != null) {
      if (!expandedIds.includes(parentId)) return false;
      const pare = partidesList.find(x => x.idPartidesPressupost === parentId);
      parentId = pare?.idPartidaPare ?? null;
    }
    return true;
  }, [expandedIds, partidesList]);


  // 🔹 Si la partida seleccionada deixa de ser visible (perquè pleguem algun pare), deseleccionar
  useEffect(() => {
    if (selectedPartida && !isPartidaVisible(selectedPartida)) {
      setSelectedPartida(null);
    }
  }, [expandedIds, selectedPartida, isPartidaVisible]);




  // Helpers per saber la "partida anterior" al mateix nivell
  const partidaAnterior = (partides, idPare) => {
     // Filtra només les que tinguin idPartidaPare igual al pareId
      const mateixNivell = partides.filter((p) => (p.idPartidaPare === idPare));
      
      if (mateixNivell.length === 0) return null;

      // Torna l’últim
      return mateixNivell[mateixNivell.length - 1];
  };

  const reFetchPartides = async () => {
    const prevSelectedId = selectedPartida?.idPartidesPressupost || null;

    const list = await fetchPartides();    // ara tenim la llista actualitzada
    setActiveTab("table");

    if (prevSelectedId && list && list.length > 0) {
      const updated = list.find(
        (p) => p.idPartidesPressupost === prevSelectedId
      );
      if (updated) {
        setSelectedPartida(updated);       // 🔹 Reenganxem la selecció a l’objecte nou
      }
    }
    // (si vols, aquí podries tocar expandedIds, però no és necessari per aquest bug)
  };

  // Determinem si una partida és familia d'una altra
  function esParent(partidesList, possibleParentId, childId) {
    let currentId = childId;
    while (currentId) {
      if (currentId === possibleParentId) return true;
      const child = partidesList.find(p => p.idPartidesPressupost === currentId);
      currentId = child?.idPartidaPare || null;
    }
    return false;
  }
  

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === "asc" ? "desc" : "asc",
    }));
  };
  
  const sortPartides = (partides, { key, direction }) => {

    // Si no hi ha cap clau, retornem la llista original
    if (!key) return partides;
  
    return [...partides].sort((a, b) => {
      let valA = a[key] ?? "";
      let valB = b[key] ?? "";
  
      // Convertim a float
      const numA = parseFloat(valA);
      const numB = parseFloat(valB);
  
      const aIsNumeric = !isNaN(numA);
      const bIsNumeric = !isNaN(numB);
  
      // Si els dos són numèrics
      if (aIsNumeric && bIsNumeric) {
        return direction === "asc" ? numA - numB : numB - numA;
      }
  
      // Si un ho és i l'altre no, pots decidir si vols que el numèric vagi primer
      // (això és opcional; aquí ho tracto com a text si algun no és numèric)
      // if (aIsNumeric && !bIsNumeric) return -1; // si vols que el numèric vagi abans
      // if (!aIsNumeric && bIsNumeric) return 1;
  
      // Comparació alfabètica (si algun no és numèric)
      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      if (strA < strB) return direction === "asc" ? -1 : 1;
      if (strA > strB) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  function filtrarPartidesAmbPares(partidesList, searchTerm) {
    if (!searchTerm || searchTerm.trim() === "") {
      // Sense filtre → tornem tot
      return partidesList;
    }
  
    const lower = searchTerm.toLowerCase();
  
    // 1) Creem un Set d'IDs que compleixen (ells o algun fill)
    const matchedIds = new Set();
  
    for (const p of partidesList) {
      const textPartida = p.Partida?.toLowerCase() || "";
      const textDesc = p.DescripcioPartida?.toLowerCase() || "";
  
      // Mirem si aquesta partida compleix el filtre
      const compleix = textPartida.includes(lower) || textDesc.includes(lower);
      if (compleix) {
        // Afegim aquesta partida
        matchedIds.add(p.idPartidesPressupost);
  
        // Pugem fins al pare (mentre no sigui null i no l’haguem afegit encara)
        let ancestorId = p.idPartidaPare;
        while (ancestorId) {
          if (matchedIds.has(ancestorId)) {
            // ja està afegit, no cal continuar pujant
            break;
          }
          matchedIds.add(ancestorId);
  
          // busquem el pare en la llista
          const pare = partidesList.find(
            (x) => x.idPartidesPressupost === ancestorId
          );
          if (!pare) break; // no ha aparegut a la llista per algun motiu
          ancestorId = pare.idPartidaPare; // continuem amunt
        }
      }
    }
  
    // 2) Retornem només les partides que estan al set
    return partidesList.filter((p) => matchedIds.has(p.idPartidesPressupost));
  }
  
  

  // --------------------------------------------------------
  // FUNCIO RECURSIVA: renderArbre(idPare, level)
  // --------------------------------------------------------
  // Retorna un bloc JSX (taula o llista) amb tots els fills d'un pare determinat.
  // Al nivell 0, idPare = null.
  // Si trobem el primer fill com a "EsApartat", tot el nivell és "Apartat".
  // Si trobem el primer fill com a "Partida", tot el nivell és partides (en taula).
  // En expandir un element, crida recursivament la mateixa funció.
  // Es fa servir "expandedIds" per a la lògica de desplegar/plegar.
  // I "selectedPartida" per a la selecció.
  function renderArbre(idPare, level) {
    const visible = filtrarPartidesAmbPares(partidesList, filtrePartida);
    const fills = visible.filter((p) => idPareVal(p.idPartidaPare) === idPareVal(idPare));
    if (fills.length === 0) return null;
    const sortedFills = sortPartides(fills, sortConfig);

    const handleRowClick = (f) => {
      setSelectedPartida((prev) =>
        prev?.idPartidesPressupost === f.idPartidesPressupost ? null : f
      );
    };

    const toggleExpand = (e, f) => {
      e.stopPropagation();
      const isExpanded = expandedIds.includes(f.idPartidesPressupost);
      if (isExpanded) {
        if (
          selectedPartida &&
          (selectedPartida.idPartidesPressupost === f.idPartidesPressupost ||
            esParent(partidesList, f.idPartidesPressupost, selectedPartida.idPartidesPressupost))
        ) {
          setSelectedPartida(null);
        }
        setExpandedIds((prev) => prev.filter((id) => id !== f.idPartidesPressupost));
      } else {
        setExpandedIds((prev) => [...prev, f.idPartidesPressupost]);
      }
    };

    const obrirMenu = (e, f) => {
      e.stopPropagation();
      const id = f.idPartidesPressupost;
      if (menuObert === id) {
        setMenuObert(null);
        return;
      }
      const rect = e.currentTarget.getBoundingClientRect();
      const ample = 220;
      const alt = 280;
      const marge = 8;
      let top = rect.bottom + 4;
      if (top + alt > window.innerHeight - marge) top = Math.max(marge, rect.top - alt - 4);
      let left = rect.right - ample;
      if (left < marge) left = marge;
      if (left + ample > window.innerWidth - marge) left = window.innerWidth - ample - marge;
      setMenuPos({ top, left });
      setMenuTipus("partida");
      setMenuObert(id);
    };

    return sortedFills.map((f) => {
      const isExpanded = expandedIds.includes(f.idPartidesPressupost);
      const isSelected = selectedPartida?.idPartidesPressupost === f.idPartidesPressupost;
      const hasChildren = teFillsPartida(f.idPartidesPressupost);
      const nest = classeNiuFila(level);
      const esApt = esItemApartat(f);
      return (
        <React.Fragment key={f.idPartidesPressupost}>
          <tr
            ref={isSelected ? selectedRowRef : null}
            className={`${isSelected ? "is-selected" : ""} ${isExpanded ? "is-open" : ""} ${nest}`.trim()}
            onClick={() => handleRowClick(f)}
            onDoubleClick={() => {
              setSelectedPartida(f);
              setActiveTab("consultar");
            }}
          >
            <td
              className="dv2-col-expand"
              style={{ paddingLeft: 8 + level * 16 }}
              onClick={(e) => toggleExpand(e, f)}
            >
              <button
                type="button"
                className={`dv2-expandBtn ${isExpanded ? "is-open" : ""} ${hasChildren ? "has-children" : ""}`}
                aria-label={isExpanded ? "Plegar" : "Expandir"}
                onClick={(e) => toggleExpand(e, f)}
              >
                {isExpanded ? "▼" : "▶"}
              </button>
            </td>
            <td>
              <strong>{f.Partida}</strong>
              {esApt ? <span className="dv2-hint"> · Apartat</span> : null}
            </td>
            <td className="dv2-num">{esApt ? "—" : formatNumero(f.Quantitat)}</td>
            <td>{esApt ? "—" : f.Unitats || "—"}</td>
            <td className="dv2-cell-clip" title={f.DescripcioPartida || ""}>
              {f.DescripcioPartida || "—"}
            </td>
            <td>{f.Categoria || "—"}</td>
            <td className="dv2-num">{esApt ? "—" : formatPreu(f.PreuUnitari)}</td>
            <td className="dv2-num">{formatPreu(f.PreuTotal)}</td>
            <td className="dv2-table-actions">
              <button
                type="button"
                className={`dv2-menuBtn ${menuObert === f.idPartidesPressupost ? "is-open" : ""}`}
                aria-label="Accions"
                onClick={(e) => obrirMenu(e, f)}
              >
                …
              </button>
            </td>
          </tr>
          {isExpanded && renderArbre(f.idPartidesPressupost, level + 1)}
        </React.Fragment>
      );
    });
  }

  function renderArbreMobil(idPare, level) {
    const visible = filtrarPartidesAmbPares(partidesList, filtrePartida);
    const fills = visible.filter((p) => idPareVal(p.idPartidaPare) === idPareVal(idPare));
    if (fills.length === 0) return null;
    const sortedFills = sortPartides(fills, sortConfig);

    return sortedFills.map((f) => {
      const isExpanded = expandedIds.includes(f.idPartidesPressupost);
      const isSelected = selectedPartida?.idPartidesPressupost === f.idPartidesPressupost;
      const hasChildren = teFillsPartida(f.idPartidesPressupost);
      const nest = classeNiuFila(level);
      const esApt = esItemApartat(f);
      return (
        <React.Fragment key={f.idPartidesPressupost}>
          <article
            className={`dv2-record ${isSelected ? "is-selected" : ""} ${nest}`.trim()}
            style={{ marginLeft: level * 12 }}
            onClick={() =>
              setSelectedPartida((prev) =>
                prev?.idPartidesPressupost === f.idPartidesPressupost ? null : f
              )
            }
            onDoubleClick={() => {
              setSelectedPartida(f);
              setActiveTab("consultar");
            }}
          >
            <div>
              <p className="dv2-record-title">
                {f.Partida || "—"}
                {esApt ? " · Apartat" : ""}
              </p>
              <p className="dv2-record-meta">
                {[
                  f.DescripcioPartida,
                  !esApt && f.Quantitat != null ? `${formatNumero(f.Quantitat)} ${f.Unitats || ""}`.trim() : null,
                  formatPreu(f.PreuTotal),
                ]
                  .filter(Boolean)
                  .join(" · ") || "—"}
              </p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button
                type="button"
                className={`dv2-expandBtn ${isExpanded ? "is-open" : ""} ${hasChildren ? "has-children" : ""}`}
                aria-label={isExpanded ? "Plegar" : "Expandir"}
                onClick={(e) => {
                  e.stopPropagation();
                  const obert = expandedIds.includes(f.idPartidesPressupost);
                  if (obert) {
                    if (
                      selectedPartida &&
                      (selectedPartida.idPartidesPressupost === f.idPartidesPressupost ||
                        esParent(partidesList, f.idPartidesPressupost, selectedPartida.idPartidesPressupost))
                    ) {
                      setSelectedPartida(null);
                    }
                    setExpandedIds((prev) => prev.filter((id) => id !== f.idPartidesPressupost));
                  } else {
                    setExpandedIds((prev) => [...prev, f.idPartidesPressupost]);
                  }
                }}
              >
                {isExpanded ? "▼" : "▶"}
              </button>
              <button
                type="button"
                className={`dv2-menuBtn ${menuObert === f.idPartidesPressupost ? "is-open" : ""}`}
                aria-label="Accions"
                onClick={(e) => {
                  e.stopPropagation();
                  const id = f.idPartidesPressupost;
                  if (menuObert === id) {
                    setMenuObert(null);
                    return;
                  }
                  const rect = e.currentTarget.getBoundingClientRect();
                  const ample = 220;
                  const alt = 280;
                  const marge = 8;
                  let top = rect.bottom + 4;
                  if (top + alt > window.innerHeight - marge) top = Math.max(marge, rect.top - alt - 4);
                  let left = rect.right - ample;
                  if (left < marge) left = marge;
                  if (left + ample > window.innerWidth - marge) left = window.innerWidth - ample - marge;
                  setMenuPos({ top, left });
                  setMenuTipus("partida");
                  setMenuObert(id);
                }}
              >
                …
              </button>
            </div>
          </article>
          {isExpanded && renderArbreMobil(f.idPartidesPressupost, level + 1)}
        </React.Fragment>
      );
    });
  }

  const lockedVersio = estat === "Presentat" || estat === "Acceptat" || estat === "Rebutjat";

  function getBotonsCreacio(sel = selectedPartida, idsExp = expandedIds) {
    const p = getPermisosCreacio(sel, idsExp);
    const botons = [];
    if (p.canCreateApartat) botons.push({ tab: "nouApartat", label: "Nou apartat" });
    if (p.canCreateSubApartat) botons.push({ tab: "nouSubApartat", label: "Nou subapartat" });
    if (p.canCreatePartida) botons.push({ tab: "nou", label: "Nova partida" });
    if (p.canCreateSubPartida) botons.push({ tab: "nouSubpartida", label: "Nova subpartida" });
    return botons;
  }

  function getAccionsFilla(p) {
    if (!p) return [];
    const ids = expandedIds.includes(p.idPartidesPressupost)
      ? expandedIds
      : [...expandedIds, p.idPartidesPressupost];
    return getBotonsCreacio(p, ids).filter((b) =>
      b.tab === "nouSubApartat" || b.tab === "nou" || b.tab === "nouSubpartida"
    );
  }

  function obrirNovaFilla(p, tab) {
    if (!p || !tab) return;
    setExpandedIds((prev) =>
      prev.includes(p.idPartidesPressupost) ? prev : [...prev, p.idPartidesPressupost]
    );
    setSelectedPartida(p);
    setMenuObert(null);
    setActiveTab(tab);
  }

  const renderInfoPlantilla = () => {
    if (!plantillaPerVersio) return null;
    return (
      <div className="dv2-banner">
        <strong>Plantilla:</strong> {plantillaPerVersio.Nom}
        {` · Nivell: ${getNivellActualText()}`}
        {` · Inserció: ${getZonaInsercioText()}`}
        <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
          <button type="button" className="dv2-btn dv2-btn--primary dv2-btn--compact" onClick={handleUsarPlantilla} disabled={lockedVersio}>
            Aplicar plantilla
          </button>
          <button type="button" className="dv2-btn dv2-btn--compact" onClick={() => setPlantillaPerVersio(null)}>
            Descartar
          </button>
        </div>
      </div>
    );
  };

  const renderArbreNivell0 = () => {
    const llistaFiltrada = filtrarPartidesAmbPares(partidesList, filtrePartida);
    const partidaMenu = menuObert ? partidesList.find((p) => p.idPartidesPressupost === menuObert) : null;
    const locked = lockedVersio;
    const botonsNou = getBotonsCreacio();
    const accionsFillaMenu = partidaMenu && !locked ? getAccionsFilla(partidaMenu) : [];
    const tancarI = (fn) => {
      setMenuObert(null);
      fn?.();
    };
    const nivellText = `Nivell: ${getNivellActualText()}`;

    return (
      <>
        {renderInfoPlantilla()}
        <div className="dv2-filterBar">
          {potCrear && botonsNou.map((boto, i) => (
            <button
              key={boto.tab}
              type="button"
              className={`dv2-btn${i === 0 ? " dv2-btn--primary" : ""}`}
              disabled={locked}
              onClick={() => setActiveTab(boto.tab)}
            >
              {boto.label}
            </button>
          ))}
          {potCrear && (
            <button
              type="button"
              className="dv2-btn"
              disabled={locked}
              onClick={() => setActiveTab("usarPlantilla")}
            >
              Usar plantilla
            </button>
          )}
          <div className="dv2-filter dv2-filter--grow">
            <span className="dv2-filter-label">Cerca partides</span>
            <input
              type="text"
              className="dv2-input dv2-input-search"
              placeholder="Número o descripció…"
              value={filtrePartida}
              onChange={(e) => setFiltrePartida(e.target.value)}
            />
          </div>
        </div>
        <p className="dv2-hint dv2-hint--clipStart" title={nivellText}>
          <span>{nivellText}</span>
        </p>
        {partidesList.length === 0 ? (
          <p className="dv2-empty">No hi ha partides ni apartats per aquesta versió.</p>
        ) : llistaFiltrada.length === 0 ? (
          <p className="dv2-empty">Cap partida no coincideix amb la cerca.</p>
        ) : isNarrow ? (
          <div className="dv2-recordList" style={{ display: "flex" }}>
            {renderArbreMobil(null, 0)}
          </div>
        ) : (
          <div className="dv2-tableWrap">
            <table className="dv2-table">
              <thead>
                <tr>
                  <th className="dv2-col-expand"></th>
                  <th onClick={() => handleSort("Partida")}>Partida</th>
                  <th className="dv2-num" onClick={() => handleSort("Quantitat")}>Quantitat</th>
                  <th>Unitats</th>
                  <th onClick={() => handleSort("DescripcioPartida")}>Descripció</th>
                  <th onClick={() => handleSort("Categoria")}>Categoria</th>
                  <th className="dv2-num" onClick={() => handleSort("PreuUnitari")}>Preu Unitari</th>
                  <th className="dv2-num" onClick={() => handleSort("PreuTotal")}>Preu Total</th>
                  <th className="dv2-table-actions"></th>
                </tr>
              </thead>
              <tbody>{renderArbre(null, 0)}</tbody>
            </table>
          </div>
        )}
        {menuTipus === "partida" && partidaMenu &&
          menuPos &&
          createPortal(
            <div className="dv2-menu dv2-menu--fixed" ref={menuRef} style={{ top: menuPos.top, left: menuPos.left }}>
              {potConsultar && (
                <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => { setSelectedPartida(partidaMenu); setActiveTab("consultar"); })}>
                  Consultar
                </button>
              )}
              {potModificar && !locked && (
                <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => { setSelectedPartida(partidaMenu); setActiveTab("modificar"); })}>
                  Modificar
                </button>
              )}
              {potCrear && accionsFillaMenu.map((accio) => (
                <button
                  key={accio.tab}
                  type="button"
                  className="dv2-menu-item"
                  onClick={() => obrirNovaFilla(partidaMenu, accio.tab)}
                >
                  {accio.label}
                </button>
              ))}
              {potEsborrar && !locked && (
                <button
                  type="button"
                  className="dv2-menu-item is-danger"
                  onClick={() => tancarI(() => { setSelectedPartida(partidaMenu); setActiveTab("esborrar"); })}
                >
                  Esborrar
                </button>
              )}
            </div>,
            document.body
          )}
      </>
    );
  };


    


  // ------------------
  // Funcions auxiliars
  // ------------------

  function hiHaPartides (partidesList, idPare) {
    return partidesList.some(p => idPareVal(p.idPartidaPare) === idPareVal(idPare) && !esItemApartat(p));
  }

  function hiHaApartats (partidesList, idPare) {
    return partidesList.some(p => idPareVal(p.idPartidaPare) === idPareVal(idPare) && esItemApartat(p));
  }

  function partidesFills (partidesList, idPare) {
    return partidesList.filter(p => idPareVal(p.idPartidaPare) === idPareVal(idPare));
  }

  function getPermisosCreacio(sel = selectedPartida, idsExp = expandedIds) {
    let canCreateApartat = false;
    let canCreatePartida = false;
    let canCreateSubApartat = false;
    let canCreateSubPartida = false;
    const isExpanded = !!(sel && idsExp.includes(sel.idPartidesPressupost));

    if (!sel) {
      const partidesNivell0 = partidesFills(partidesList, null);
      if (partidesNivell0.length === 0) {
        canCreateApartat = true;
        canCreatePartida = true;
      } else if (hiHaApartats(partidesNivell0, null)) {
        canCreateApartat = true;
      } else if (hiHaPartides(partidesNivell0, null)) {
        canCreatePartida = true;
      }
    } else if (idPareVal(sel.idPartidaPare) == null) {
      if (!isExpanded) {
        const partidesNivell0 = partidesFills(partidesList, null);
        if (partidesNivell0.length === 0) {
          canCreateApartat = true;
          canCreatePartida = true;
        } else if (hiHaApartats(partidesNivell0, null)) {
          canCreateApartat = true;
        } else if (hiHaPartides(partidesNivell0, null)) {
          canCreatePartida = true;
        }
      } else {
        const partidesNivell1 = partidesFills(partidesList, sel.idPartidesPressupost);
        if (hiHaApartats(partidesNivell1, sel.idPartidesPressupost)) {
          canCreateSubApartat = true;
        } else if (hiHaPartides(partidesNivell1, sel.idPartidesPressupost)) {
          if (esItemApartat(sel)) canCreatePartida = true;
          else canCreateSubPartida = true;
        } else if (esItemApartat(sel)) {
          canCreateSubApartat = true;
          canCreatePartida = true;
        } else {
          canCreateSubPartida = true;
        }
      }
    } else if (!isExpanded) {
      const pare = partidesList.find((p) => p.idPartidesPressupost === sel.idPartidaPare);
      if (esItemApartat(pare)) {
        if (esItemApartat(sel)) canCreateSubApartat = true;
        else canCreatePartida = true;
      } else {
        canCreateSubPartida = true;
      }
    } else if (esItemApartat(sel)) {
      const partidesNivellSeg = partidesFills(partidesList, sel.idPartidesPressupost);
      if (hiHaApartats(partidesNivellSeg, sel.idPartidesPressupost)) {
        canCreateSubApartat = true;
      } else if (hiHaPartides(partidesNivellSeg, sel.idPartidesPressupost)) {
        canCreatePartida = true;
      } else {
        canCreateSubApartat = true;
        canCreatePartida = true;
      }
    } else {
      canCreateSubPartida = true;
    }

    return { canCreateApartat, canCreatePartida, canCreateSubApartat, canCreateSubPartida };
  }

  const teFillsPartida = (id) => partidesList.some((p) => idPareVal(p.idPartidaPare) === idPareVal(id));

  const getPareNivellActual = () => {
    if (!selectedPartida) return null;
    const isExpanded = expandedIds.includes(selectedPartida.idPartidesPressupost);
    if (isExpanded) return selectedPartida.idPartidesPressupost;
    return selectedPartida.idPartidaPare || null;
  };

  const getNivellActualText = () => {
    const pareId = getPareNivellActual();
    if (pareId === null) return "0";
    const chain = [];
    let currentId = pareId;
    while (currentId) {
      const p = partidesList.find(x => x.idPartidesPressupost === currentId);
      if (!p) break;
      const tipus = esItemApartat(p) ? "Apartat" : "Partida";
      const desc = p.DescripcioPartida ? ` ${p.DescripcioPartida.substring(0, 30)}` : "";
      chain.unshift(`${tipus} ${p.Partida}${desc}`);
      currentId = p.idPartidaPare;
    }
    return chain.join(" / ");
  };

  const getZonaInsercioText = () => {
    const idPareActual = getPareNivellActual();
    const fills = partidesFills(partidesList, idPareActual)
      .sort((a, b) => compararPartida(a.Partida, b.Partida));

    if (selectedPartida && !expandedIds.includes(selectedPartida.idPartidesPressupost)) {
      const tipusSel = esItemApartat(selectedPartida) ? "Apartat" : "Partida";
      const descSel = selectedPartida.DescripcioPartida
        ? ` ${selectedPartida.DescripcioPartida.substring(0, 30)}`
        : "";
      return `Després de ${tipusSel} ${selectedPartida.Partida}${descSel}`;
    }

    if (fills.length === 0) {
      if (idPareActual) {
        const pare = partidesList.find(x => x.idPartidesPressupost === idPareActual);
        if (pare) {
          const tipusP = esItemApartat(pare) ? "Apartat" : "Partida";
          return `Primera posició dins de ${tipusP} ${pare.Partida}`;
        }
      }
      return "Primera posició (arrel buida)";
    }

    const ultim = fills[fills.length - 1];
    const tipusU = esItemApartat(ultim) ? "Apartat" : "Partida";
    const descU = ultim.DescripcioPartida
      ? ` ${ultim.DescripcioPartida.substring(0, 30)}`
      : "";
    return `Després de ${tipusU} ${ultim.Partida}${descU}`;
  };

  const getInsertionContext = () => {
    const idPareActual = getPareNivellActual();

    let insertAfterPartidaId = null;
    if (selectedPartida && !expandedIds.includes(selectedPartida.idPartidesPressupost)) {
      insertAfterPartidaId = selectedPartida.idPartidesPressupost;
    } else {
      const fills = partidesFills(partidesList, idPareActual)
        .sort((a, b) => compararPartida(a.Partida, b.Partida));
      if (fills.length > 0) {
        insertAfterPartidaId = fills[fills.length - 1].idPartidesPressupost;
      }
    }

    return { idPartidaPare: idPareActual, insertAfterPartidaId };
  };

  const handleSelectPlantillaPerVersio = (plantilla) => {
    setPlantillaPerVersio(plantilla);
    setActiveTab("table");
  };

  const handleUsarPlantilla = async () => {
    if (!plantillaPerVersio || !selectedVersion) return;
    const { idPartidaPare, insertAfterPartidaId } = getInsertionContext();

    try {
      const resp = await fetch(`${API_URL}/Aplicarplantilla.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idPlantilla: parseInt(plantillaPerVersio.id),
          idVersions: selectedVersion.idVersions,
          idPressupostos: pressupost.idPressupostos,
          idEmpresa: empresaSeleccionada?.IdEmpresa || 0,
          idPartidaPare: idPartidaPare,
          insertAfterPartidaId: insertAfterPartidaId,
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setPlantillaPerVersio(null);
        await reFetchPartides();
        refreshVersionsAndKeepSelected();
        alert("Plantilla aplicada correctament.");
      } else {
        alert(data.message || "Error aplicant la plantilla.");
      }
    } catch (err) {
      console.error("Error aplicant plantilla:", err);
      alert("Error de connexió aplicant la plantilla.");
    }
  };

  // ---------------------------------------------
  // RENDER DEL CONTINGUT (SEGONS PESTANYA)
  // ---------------------------------------------
  const renderActiveTab = () => {

    const getPartidaPare = () => {
      if (!selectedPartida) return null;
      const expandida = expandedIds.includes(selectedPartida.idPartidesPressupost);
      if (expandida) {
        return selectedPartida;
      }
      else {
        if (idPareVal(selectedPartida.idPartidaPare) == null) {return null}
        else { 
          return partidesList.find(p => p.idPartidesPressupost === selectedPartida.idPartidaPare);
        }
      }
    };

    if (!selectedVersion) return null;
  
    // Calculem la partida pare correctament
    const partidaPare = getPartidaPare();
  
    switch (activeTab) {
      case "table":
        return renderArbreNivell0();
  
      case "nouApartat":
      case "nouSubApartat":
        return (
          <DetallsPartidaVersio
            mode="nou"
            isApartat={true}
            pressupost={pressupost}
            versio={selectedVersion}
            partidaPare={partidaPare}
            partida={partidaAnterior(partidesList, partidaPare?.idPartidesPressupost || null)}
            onSave={() => {
              reFetchPartides();
              refreshVersionsAndKeepSelected();
            }}
            onCancel={() => setActiveTab("table")}
          />
        );
  
      case "nou":
      case "nouSubpartida":
        return (
          <DetallsPartidaVersio
            mode="nou"
            isApartat={false}
            pressupost={pressupost}
            versio={selectedVersion}
            partidaPare={partidaPare}
            partida={partidaAnterior(partidesList, partidaPare?.idPartidesPressupost || null)}
            onSave={() => {
              reFetchPartides();
              refreshVersionsAndKeepSelected();
            }}
            onCancel={() => setActiveTab("table")}
          />
        );
  
      case "consultar":
      case "consultarSubpartida":
        if (!selectedPartida) return null;
        return (
          <DetallsPartidaVersio
            key={selectedPartida.idPartidesPressupost}
            mode="consultar"
            isApartat={esItemApartat(selectedPartida)}
            pressupost={pressupost}
            versio={selectedVersion}
            partidaPare={partidaPare}
            partida={selectedPartida}
            partidesList={partidesList}
            onNavigate={(p) => setSelectedPartida(p)}
            onCancel={() => setActiveTab("table")}
          />
        );
  
      case "modificar":
      case "modificarSubpartida":
        if (!selectedPartida) return null;
        return (
          <DetallsPartidaVersio
            key={selectedPartida.idPartidesPressupost}
            mode="modificar"
            isApartat={esItemApartat(selectedPartida)}
            pressupost={pressupost}
            versio={selectedVersion}
            partidaPare={partidaPare}
            partida={selectedPartida}
            partidesList={partidesList}
            onNavigate={(p) => setSelectedPartida(p)}
            onSave={() => {
              reFetchPartides();
              refreshVersionsAndKeepSelected();
            }}
            onCancel={() => setActiveTab("table")}
          />
        );
  
      case "esborrar":
      case "esborrarSubpartida":
        if (!selectedPartida) return null;
        return (
          <DetallsPartidaVersio
            key={selectedPartida.idPartidesPressupost}
            mode="esborrar"
            isApartat={esItemApartat(selectedPartida)}
            pressupost={pressupost}
            versio={selectedVersion}
            partidaPare={partidaPare}
            partida={selectedPartida}
            partidesList={partidesList}
            onNavigate={(p) => setSelectedPartida(p)}
            onCancel={() => {
              reFetchPartides();
              refreshVersionsAndKeepSelected();
              setActiveTab("table");
            }}
          />
        );
  
      case "usarPlantilla":
        return (
          <div className="dv2-entity-body">
            <PlantillesExplorador
              idEmpresa={empresaSeleccionada?.IdEmpresa || 0}
              selectedPlantilla={peSel}
              onSelectPlantilla={setPeSel}
              selectedCategoria={peCat}
              onSelectCategoria={setPeCat}
              expandedNodes={peExp}
              onExpandedNodesChange={setPeExp}
              onGoToPartides={() => {}}
              mode="select"
              onSelectForVersion={handleSelectPlantillaPerVersio}
            />
          </div>
        );

      default:
        return renderArbreNivell0();
    }
  };


  // ---------------------------------------------
  // RENDERS de la CAPÇALERA (versions, dates, etc)
  // ---------------------------------------------
  function descendentsPartida(partidesList, rootId) {
    const resultat = [];

    function visit(id) {
      resultat.push(id);
      const fills = partidesList.filter((p) => p.idPartidaPare === id);
      fills.forEach((f) => visit(f.idPartidesPressupost));
    }

    visit(rootId);
    return resultat;
  }

  /* Calcula el cost d'UNA partida, sense mirar subpartides.
   * S’assumeix que la partida ja porta dins d’`Preu...Heretat`
   * tot el que calgui (fills, nets, etc.).
   */
  function calcularTotalsPartida(partida) {
    let costDirecteMaObra = 0;
    if ((partida.PreuPrioritariMaObra ?? "") === "Manual") {
      costDirecteMaObra = parseFloat(partida.PreuMaObraManual ?? 0);
    } else {
      costDirecteMaObra = parseFloat(partida.PreuMaObra ?? 0);
    }

    let costDirecteMaterial = 0;
    if ((partida.PreuPrioritariMaterial ?? "") === "Manual") {
      costDirecteMaterial = parseFloat(partida.PreuMaterialManual ?? 0);
    } else {
      costDirecteMaterial = parseFloat(partida.PreuMaterialProducteProveidor ?? 0);
    }

    const costDirecteExtra = parseFloat(partida.PreuExtra ?? 0);
    const costMaObraArrel = costDirecteMaObra + parseFloat(partida.PreuMaObraHeretat ?? 0);
    const costMaterialArrel = costDirecteMaterial + parseFloat(partida.PreuMaterialHeretat ?? 0);
    const costExtraArrel = costDirecteExtra + parseFloat(partida.PreuExtraHeretat ?? 0);

    const quant = parseFloat(partida.Quantitat ?? 0);
    const subMaObra = round2(costMaObraArrel * quant);
    const subMaterial = round2(costMaterialArrel * quant);
    const subExtra = round2(costExtraArrel * quant);
    const totalGlobal = round2(subMaObra + subMaterial + subExtra);

    return {
      PreuMaObra: subMaObra,
      PreuMaterials: subMaterial,
      PreuExtra: subExtra,
      PreuCost: totalGlobal,
    };
  }

  function round2(n) {
    return Math.round(n * 100) / 100;
  }

  const tancarCopia = () => {
    setIsCopyingVersion(false);
    setCopyPas(null);
    setSelectedCopyPressupost(null);
  };

  const carregarVersio = (vers) => {
    setSelectedVersion(vers || null);
    setEstat(vers?.Estat || "Esborrany");
    setComentaris(vers?.Comentaris || "");
    setMenuAccions(false);
  };

  const obrirFitxaVersio = (vers, mode) => {
    carregarVersio(vers);
    setModeVersio(mode);
    setVista("fitxa");
    setMenuObert(null);
  };

  const obrirNovaVersio = () => {
    carregarVersio(null);
    setEstat("Esborrany");
    setComentaris("");
    setModeVersio("nou");
    setVista("fitxa");
  };

  const obrirPartides = (vers, origen = "llista") => {
    if (!vers) return;
    carregarVersio(vers);
    setOrigenPartides(origen);
    setActiveTab("table");
    setSelectedPartida(null);
    setPlantillaPerVersio(null);
    setVista("partides");
    setMenuObert(null);
  };

  const obrirCopia = (vers) => {
    carregarVersio(vers);
    setCopyPas("tria");
    setCopyFiltre("");
    setSelectedCopyPressupost(null);
    setIsCopyingVersion(true);
    setMenuObert(null);
    setMenuAccions(false);
  };

  const tornarLlistaVersions = () => {
    setVista("llista");
    setModeVersio("consultar");
    setMenuAccions(false);
    setActiveTab("table");
    setSelectedPartida(null);
  };

  const tornarUnPas = () => {
    if (isCopyingVersion) {
      if (copyPas === "altre") {
        setCopyPas("tria");
        return;
      }
      tancarCopia();
      return;
    }
    if (vista === "partides") {
      if (activeTab !== "table") {
        setActiveTab("table");
        return;
      }
      if (origenPartides === "fitxa") {
        setModeVersio("consultar");
        setVista("fitxa");
        return;
      }
      tornarLlistaVersions();
      return;
    }
    if (vista === "fitxa") {
      tornarLlistaVersions();
      return;
    }
    onCancel?.();
  };
  TeclaEscEnter(tornarUnPas, null);

  const desarFitxaVersio = async () => {
    if (modeVersio === "nou") {
      const nova = await createVersion();
      if (!nova) {
        alert("No s'ha pogut crear la versió.");
        return;
      }
      const comentariFinal = (comentaris || "").trim() || "Sense comentaris";
      const estatFinal = estat || "Esborrany";
      if (estatFinal !== (nova.Estat || "Esborrany") || comentariFinal !== (nova.Comentaris || "Sense comentaris")) {
        await updateVersion(nova, estatFinal, comentariFinal);
        const { list } = await fetchVersions();
        const actualitzada = list.find((v) => Number(v.idVersions) === Number(nova.idVersions)) || nova;
        carregarVersio(actualitzada);
      } else {
        carregarVersio(nova);
      }
      setModeVersio("consultar");
      return;
    }
    if (modeVersio === "modificar") {
      await updateVersion();
      await refreshVersionsAndKeepSelected();
      setModeVersio("consultar");
    }
  };

  const confirmarEsborrarVersio = async () => {
    await deleteVersion();
    tornarLlistaVersions();
  };

  const versionsFiltrades = (() => {
    let rows = versionsList;
    if (filtreEstatVersio) {
      rows = rows.filter((v) => v.Estat === filtreEstatVersio);
    }
    const { key, direction } = sortVersions;
    if (!key) return rows;
    const dir = direction === "asc" ? 1 : -1;
    const nums = new Set(["idVersions", "PreuCost", "PreuMaObra", "PreuMaterials", "PreuExtra"]);
    return [...rows].sort((a, b) => {
      if (key === "dataEstat") {
        const va = dataSegonsEstat(a, a.Estat).valor || "";
        const vb = dataSegonsEstat(b, b.Estat).valor || "";
        return va.localeCompare(vb, "ca", { numeric: true, sensitivity: "base" }) * dir;
      }
      if (nums.has(key)) return ((Number(a[key]) || 0) - (Number(b[key]) || 0)) * dir;
      const va = (a[key] ?? "").toString();
      const vb = (b[key] ?? "").toString();
      return va.localeCompare(vb, "ca", { numeric: true, sensitivity: "base" }) * dir;
    });
  })();

  const handleSortVersions = (key) => {
    setSortVersions((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const obrirMenuVersio = (e, vers) => {
    e.stopPropagation();
    const id = vers.idVersions;
    if (menuObert === id && menuTipus === "versio") {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 220;
    const alt = 280;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) top = Math.max(marge, rect.top - alt - 4);
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) left = window.innerWidth - ample - marge;
    setMenuPos({ top, left });
    setMenuTipus("versio");
    setMenuObert(id);
  };

  const versioMenu = menuTipus === "versio" && menuObert != null
    ? versionsList.find((v) => Number(v.idVersions) === Number(menuObert))
    : null;

  const indexVersio = selectedVersion
    ? versionsList.findIndex((v) => Number(v.idVersions) === Number(selectedVersion.idVersions))
    : -1;

  const navegarVersio = (dir) => {
    if (indexVersio < 0 || versionsList.length < 2) return;
    const next = (indexVersio + dir + versionsList.length) % versionsList.length;
    carregarVersio(versionsList[next]);
  };

  const renderLlistaVersions = () => {
    const tancarI = (fn) => {
      setMenuObert(null);
      fn?.();
    };
    return (
      <div className="dv2-entity-body">
        <div className="dv2-filterBar">
          <label className="dv2-filter">
            <span className="dv2-filter-label">Estat</span>
            <select className="dv2-select" value={filtreEstatVersio} onChange={(e) => setFiltreEstatVersio(e.target.value)}>
              <option value="">Tots</option>
              {Object.entries(mapejos.estatVersio).map(([dbValue, label]) => (
                <option key={dbValue} value={dbValue}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
        {versionsFiltrades.length === 0 ? (
          <p className="dv2-empty">No hi ha versions{filtreEstatVersio ? " amb aquest estat" : ""}.</p>
        ) : isNarrow ? (
          <div className="dv2-recordList" style={{ display: "flex" }}>
            {versionsFiltrades.map((v) => {
              const isSelected = Number(selectedVersion?.idVersions) === Number(v.idVersions);
              return (
                <article
                  key={v.idVersions}
                  className={`dv2-record ${isSelected ? "is-selected" : ""}`}
                  onClick={() => setSelectedVersion(isSelected ? null : v)}
                  onDoubleClick={() => potConsultar && obrirFitxaVersio(v, "consultar")}
                >
                  <div>
                    <p className="dv2-record-title">
                      Versió {v.idVersions}
                      {v.Estat ? ` · ${mapejos.estatVersio[v.Estat] || v.Estat}` : ""}
                    </p>
                    <p className="dv2-record-meta">
                      {[
                        formatDate(v.DataAlta),
                        v.Comentaris,
                        formatPreu(v.PreuCost),
                      ]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </p>
                  </div>
                  <button
                    type="button"
                    className={`dv2-menuBtn ${menuObert === v.idVersions && menuTipus === "versio" ? "is-open" : ""}`}
                    aria-label="Accions"
                    onClick={(e) => obrirMenuVersio(e, v)}
                  >
                    …
                  </button>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="dv2-tableWrap">
            <table className="dv2-table">
              <thead>
                <tr>
                  <th style={{ width: "1%" }} onClick={() => handleSortVersions("DataAlta")}>Data alta</th>
                  <th style={{ width: "1%" }} onClick={() => handleSortVersions("idVersions")}>Versió</th>
                  <th style={{ width: "1%" }} onClick={() => handleSortVersions("Estat")}>Estat</th>
                  <th style={{ width: "1%" }} onClick={() => handleSortVersions("dataEstat")}>Data estat</th>
                  <th onClick={() => handleSortVersions("Comentaris")}>Comentaris</th>
                  <th className="dv2-num" style={{ width: "1%" }} onClick={() => handleSortVersions("PreuMaObra")}>Mà d'obra</th>
                  <th className="dv2-num" style={{ width: "1%" }} onClick={() => handleSortVersions("PreuMaterials")}>Materials</th>
                  <th className="dv2-num" style={{ width: "1%" }} onClick={() => handleSortVersions("PreuExtra")}>Extra</th>
                  <th className="dv2-num" style={{ width: "1%" }} onClick={() => handleSortVersions("PreuCost")}>Preu cost</th>
                  <th className="dv2-table-actions"></th>
                </tr>
              </thead>
              <tbody>
                {versionsFiltrades.map((v) => {
                  const isSelected = Number(selectedVersion?.idVersions) === Number(v.idVersions);
                  return (
                    <tr
                      key={v.idVersions}
                      className={isSelected ? "is-selected" : ""}
                      onClick={() => setSelectedVersion(isSelected ? null : v)}
                      onDoubleClick={() => potConsultar && obrirFitxaVersio(v, "consultar")}
                    >
                      <td style={{ width: "1%" }}>{formatDate(v.DataAlta)}</td>
                      <td style={{ width: "1%" }}><strong>{v.idVersions}</strong></td>
                      <td style={{ width: "1%" }}>{mapejos.estatVersio[v.Estat] || v.Estat || "—"}</td>
                      <td style={{ width: "1%" }}>{formatDate(dataSegonsEstat(v, v.Estat).valor) || "—"}</td>
                      <td className="dv2-cell-clip" title={v.Comentaris || ""}>
                        {v.Comentaris || "—"}
                      </td>
                      <td className="dv2-num" style={{ width: "1%" }}>{formatPreu(v.PreuMaObra)}</td>
                      <td className="dv2-num" style={{ width: "1%" }}>{formatPreu(v.PreuMaterials)}</td>
                      <td className="dv2-num" style={{ width: "1%" }}>{formatPreu(v.PreuExtra)}</td>
                      <td className="dv2-num" style={{ width: "1%" }}>{formatPreu(v.PreuCost)}</td>
                      <td className="dv2-table-actions">
                        <button
                          type="button"
                          className={`dv2-menuBtn ${menuObert === v.idVersions && menuTipus === "versio" ? "is-open" : ""}`}
                          aria-label="Accions"
                          onClick={(e) => obrirMenuVersio(e, v)}
                        >
                          …
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {versioMenu &&
          menuPos &&
          createPortal(
            <div className="dv2-menu dv2-menu--fixed" ref={menuRef} style={{ top: menuPos.top, left: menuPos.left }}>
              {potConsultar && (
                <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => obrirFitxaVersio(versioMenu, "consultar"))}>
                  Consultar
                </button>
              )}
              {potModificar && (
                <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => obrirFitxaVersio(versioMenu, "modificar"))}>
                  Modificar
                </button>
              )}
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => obrirPartides(versioMenu, "llista"))}>
                Partides
              </button>
              {potCrear && (
                <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => obrirCopia(versioMenu))}>
                  Copiar
                </button>
              )}
              {potEsborrar && (
                <button
                  type="button"
                  className="dv2-menu-item is-danger"
                  onClick={() => tancarI(() => obrirFitxaVersio(versioMenu, "esborrar"))}
                >
                  Esborrar
                </button>
              )}
            </div>,
            document.body
          )}
      </div>
    );
  };

  const esEdicioVersio = modeVersio === "nou" || modeVersio === "modificar";

  const renderFitxaVersio = () => {
    return (
      <>
        {modeVersio === "esborrar" && (
          <div className="dv2-banner dv2-banner--danger">
            Aquesta acció no es pot desfer. Si la versió té partides, el servidor pot impedir l'esborrat.
          </div>
        )}
        <div className="dv2-entity-body">
          <div className="dv2-card">
            <div className="dv2-fields dv2-fields--sizes">
              <div className="dv2-field dv2-field--curt">
                <span className="dv2-label">Versió</span>
                <span className="dv2-value">{modeVersio === "nou" ? "S'assignarà en desar" : selectedVersion?.idVersions ?? "—"}</span>
              </div>
              <div className="dv2-field dv2-field--mig">
                <span className="dv2-label">Data alta</span>
                <span className="dv2-value">{modeVersio === "nou" ? "—" : formatDate(selectedVersion?.DataAlta)}</span>
              </div>
              <div className="dv2-field dv2-field--mig">
                <span className="dv2-label">Estat</span>
                {esEdicioVersio ? (
                  <select className="dv2-select" value={estat} onChange={(e) => setEstat(e.target.value)}>
                    {Object.entries(mapejos.estatVersio).map(([dbValue, label]) => (
                      <option key={dbValue} value={dbValue}>
                        {label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="dv2-value">{mapejos.estatVersio[estat] || estat || "—"}</span>
                )}
              </div>
              {modeVersio !== "nou" && (
                <div className="dv2-field dv2-field--mig">
                  <span className="dv2-label">{dataSegonsEstat(selectedVersion, estat).etiqueta}</span>
                  <span className="dv2-value">{formatDate(dataSegonsEstat(selectedVersion, estat).valor) || "—"}</span>
                </div>
              )}
              <span className="dv2-fields-break" aria-hidden="true" />
              <div className="dv2-field dv2-field--ample">
                <span className="dv2-label">Comentaris</span>
                {esEdicioVersio ? (
                  <textarea className="dv2-textarea" rows={3} value={comentaris} onChange={(e) => setComentaris(e.target.value)} />
                ) : (
                  <span className="dv2-value">{comentaris || "—"}</span>
                )}
              </div>
              {modeVersio !== "nou" && (
                <>
                  <span className="dv2-fields-break" aria-hidden="true" />
                  <div className="dv2-field dv2-field--curt">
                    <span className="dv2-label">Mà d'obra</span>
                    <span className="dv2-value">{formatPreu(selectedVersion?.PreuMaObra)}</span>
                  </div>
                  <div className="dv2-field dv2-field--curt">
                    <span className="dv2-label">Materials</span>
                    <span className="dv2-value">{formatPreu(selectedVersion?.PreuMaterials)}</span>
                  </div>
                  <div className="dv2-field dv2-field--curt">
                    <span className="dv2-label">Extra</span>
                    <span className="dv2-value">{formatPreu(selectedVersion?.PreuExtra)}</span>
                  </div>
                  <div className="dv2-field dv2-field--curt">
                    <span className="dv2-label">Preu cost</span>
                    <span className="dv2-value">{formatPreu(selectedVersion?.PreuCost)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {(modeVersio === "consultar" && versionsList.length > 1) || esEdicioVersio || modeVersio === "esborrar" ? (
          <div className="dv2-entity-dock">
            {modeVersio === "consultar" && versionsList.length > 1 && (
              <div className="dv2-recordNav">
                <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={() => navegarVersio(-1)} title="Anterior">
                  ‹
                </button>
                <span className="dv2-recordNav-info">
                  {indexVersio + 1} / {versionsList.length}
                </span>
                <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={() => navegarVersio(1)} title="Següent">
                  ›
                </button>
              </div>
            )}
            {(esEdicioVersio || modeVersio === "esborrar") && (
              <div className="dv2-footerActions">
                <button
                  type="button"
                  className="dv2-btn"
                  onClick={() => {
                    if (modeVersio === "modificar" && selectedVersion) {
                      carregarVersio(selectedVersion);
                      setModeVersio("consultar");
                      return;
                    }
                    tornarLlistaVersions();
                  }}
                >
                  Cancel·lar
                </button>
                {modeVersio === "esborrar" ? (
                  <button type="button" className="dv2-btn dv2-btn--danger" onClick={confirmarEsborrarVersio}>
                    Esborrar versió
                  </button>
                ) : (
                  <button type="button" className="dv2-btn dv2-btn--primary" onClick={desarFitxaVersio}>
                    {modeVersio === "nou" ? "Desar versió" : "Desar"}
                  </button>
                )}
              </div>
            )}
          </div>
        ) : null}
      </>
    );
  };

  const renderCopiaVersio = () => (
    <div className="dv2-entity-body">
      {copyPas !== "altre" ? (
        <div className="dv2-card">
          <p className="dv2-hint">
            Es copien totes les partides i apartats de la versió {selectedVersion?.idVersions}.
            La còpia sempre es crea com a <strong>Esborrany</strong>, amb el comentari «Còpia de versió».
          </p>
          <div className="dv2-card" style={{ marginTop: 12 }}>
            <strong>Copiar dins d’aquest pressupost</strong>
            <p className="dv2-hint">
              Destí: {pressupost?.idPresIntern}. Si encara no hi ha versions, serà la versió 1.
              Si l’última versió no té cap partida, s’hi reutilitza aquella versió buida.
              Si l’última ja té partides, es crea el número següent.
            </p>
            <button
              type="button"
              className="dv2-btn dv2-btn--primary"
              onClick={async () => {
                const ok = await copyVersion(pressupost.idPressupostos);
                if (ok) {
                  tancarCopia();
                  setVista("llista");
                }
              }}
            >
              Copiar aquí
            </button>
          </div>
          <div className="dv2-card" style={{ marginTop: 12 }}>
            <strong>Copiar a un altre pressupost</strong>
            <p className="dv2-hint">
              Tria el pressupost de destí. Les mateixes regles: versió 1 si el destí és buit,
              reutilitza l’última si no té partides, o crea el número següent.
            </p>
            <button type="button" className="dv2-btn" onClick={() => setCopyPas("altre")}>
              Triar pressupost…
            </button>
          </div>
        </div>
      ) : (
        <div className="dv2-card">
          <p className="dv2-hint">
            Tria el pressupost on vols copiar la versió {selectedVersion?.idVersions} de {pressupost?.idPresIntern}.
          </p>
          <div style={{ maxHeight: "50vh", overflow: "auto" }}>
            <Seleccionarpressupost
              onPressupostSelect={setSelectedCopyPressupost}
              selectedPressupost={selectedCopyPressupost}
              filters={{
                filterText: copyFiltre,
                setFilterText: setCopyFiltre,
                estatFilters: "",
                setEstatFilters: () => {},
                clientFilter: null,
                setClientFilter: () => {},
              }}
            />
          </div>
          <div className="dv2-filterBar" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="dv2-btn dv2-btn--primary"
              disabled={!selectedCopyPressupost?.idPressupostos}
              onClick={async () => {
                const ok = await copyVersion(selectedCopyPressupost.idPressupostos);
                if (ok) {
                  tancarCopia();
                  setVista("llista");
                }
              }}
            >
              Copiar
            </button>
            <button type="button" className="dv2-btn" onClick={() => setCopyPas("tria")}>
              Enrere
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const titolPagina = () => {
    if (isCopyingVersion) return "Copiar versió";
    if (vista === "fitxa") {
      if (modeVersio === "nou") return "Nova versió";
      if (modeVersio === "modificar") return "Modificar versió";
      if (modeVersio === "esborrar") return "Esborrar versió";
      return "Versió";
    }
    if (vista === "partides") {
      switch (activeTab) {
        case "nouApartat":
          return "Nou apartat";
        case "nouSubApartat":
          return "Nou subapartat";
        case "nou":
          return "Nova partida";
        case "nouSubpartida":
          return "Nova subpartida";
        case "consultar":
        case "consultarSubpartida":
          return esItemApartat(selectedPartida) ? "Apartat" : "Partida";
        case "modificar":
        case "modificarSubpartida":
          return esItemApartat(selectedPartida) ? "Modificar apartat" : "Modificar partida";
        case "esborrar":
        case "esborrarSubpartida":
          return esItemApartat(selectedPartida) ? "Esborrar apartat" : "Esborrar partida";
        case "usarPlantilla":
          return "Plantilles";
        default:
          return "Partides";
      }
    }
    return "Versions";
  };

  const subtitolPagina = (() => {
    const descripcio = String(pressupost?.Comentaris || "").replace(/\s+/g, " ").trim();
    const base = [pressupost?.idPresIntern, pressupost?.NomClient, descripcio].filter(Boolean);
    if (vista === "partides" && selectedVersion) {
      const preus = selectedPartida
        ? calcularTotalsPartida(selectedPartida)
        : {
            PreuMaObra: selectedVersion.PreuMaObra,
            PreuMaterials: selectedVersion.PreuMaterials,
            PreuExtra: selectedVersion.PreuExtra,
            PreuCost: selectedVersion.PreuCost,
          };
      return [
        ...base,
        `Versió ${selectedVersion.idVersions}`,
        mapejos.estatVersio[estat] || estat,
        selectedPartida
          ? `${esItemApartat(selectedPartida) ? "Apartat" : "Partida"} ${selectedPartida.Partida}`
          : null,
        liniaPreus(preus),
      ]
        .filter(Boolean)
        .join(" · ");
    }
    if ((vista === "fitxa" || isCopyingVersion) && selectedVersion) {
      return [...base, `Versió ${selectedVersion.idVersions}`].join(" · ");
    }
    return base.join(" · ");
  })();

  const esEsborrar =
    (vista === "fitxa" && modeVersio === "esborrar") ||
    activeTab === "esborrar" ||
    activeTab === "esborrarSubpartida";

  const esFitxaPartida = vista === "partides" && activeTab !== "table";
  const esConsultaPartida =
    vista === "partides" &&
    (activeTab === "consultar" || activeTab === "consultarSubpartida");

  return (
    <div className={subnav ? "dv2-entity" : "dv2 dv2-entity"}>
      {mostraAjuda && (
        <AjudaPressupost
          pestanya={vista === "partides" ? "partides" : "versions"}
          onTancar={() => setMostraAjuda(false)}
        />
      )}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className={`dv2-pageHeader-icon ${esEsborrar ? "is-danger" : ""}`} aria-hidden="true">
            {esEsborrar ? "🗑️" : "📄"}
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">{titolPagina()}</h1>
            <p className="dv2-pageHeader-subtitle">{subtitolPagina}</p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <button
            type="button"
            className="dv2-btn dv2-btn--icon dv2-btn--ghost"
            title="Com funcionen les Versions"
            aria-label="Ajuda"
            onClick={() => setMostraAjuda(true)}
          >
            ?
          </button>
          <button type="button" className="dv2-btn" onClick={tornarUnPas}>
            Tornar
          </button>
          {vista === "llista" && !isCopyingVersion && potCrear && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={obrirNovaVersio}>
              Nova versió
            </button>
          )}
          {vista === "partides" && esConsultaPartida && potModificar && !lockedVersio && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={() => { setMenuAccions(false); setActiveTab("modificar"); }}>
              Modificar
            </button>
          )}
          {vista === "partides" && esConsultaPartida && potEsborrar && !lockedVersio && (
            <div style={{ position: "relative" }}>
              <button type="button" className="dv2-btn" onClick={() => setMenuAccions((v) => !v)}>
                Més accions
              </button>
              {menuAccions && (
                <div className="dv2-menu" style={{ right: 0, top: "42px" }}>
                  <button
                    type="button"
                    className="dv2-menu-item is-danger"
                    onClick={() => {
                      setMenuAccions(false);
                      setActiveTab("esborrar");
                    }}
                  >
                    Esborrar
                  </button>
                </div>
              )}
            </div>
          )}
          {vista === "fitxa" && modeVersio === "consultar" && potModificar && (
            <button type="button" className="dv2-btn" onClick={() => setModeVersio("modificar")}>
              Modificar
            </button>
          )}
          {vista === "fitxa" && modeVersio === "consultar" && selectedVersion && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={() => obrirPartides(selectedVersion, "fitxa")}>
              Partides
            </button>
          )}
          {vista === "fitxa" && modeVersio === "consultar" && (
            <div style={{ position: "relative" }}>
              <button type="button" className="dv2-btn" onClick={() => setMenuAccions((v) => !v)}>
                Més accions
              </button>
              {menuAccions && (
                <div className="dv2-menu" style={{ right: 0, top: "42px" }}>
                  {potCrear && (
                    <button
                      type="button"
                      className="dv2-menu-item"
                      onClick={() => obrirCopia(selectedVersion)}
                    >
                      Copiar
                    </button>
                  )}
                  {potEsborrar && (
                    <button
                      type="button"
                      className="dv2-menu-item is-danger"
                      onClick={() => {
                        setMenuAccions(false);
                        setModeVersio("esborrar");
                      }}
                    >
                      Esborrar
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {vista === "fitxa" && esEdicioVersio && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={desarFitxaVersio}>
              Desar
            </button>
          )}
          {vista === "fitxa" && modeVersio === "esborrar" && (
            <button type="button" className="dv2-btn dv2-btn--danger" onClick={confirmarEsborrarVersio}>
              Esborrar versió
            </button>
          )}
        </div>
      </div>
      {subnav}
      {isCopyingVersion ? (
        renderCopiaVersio()
      ) : vista === "fitxa" ? (
        renderFitxaVersio()
      ) : vista === "partides" ? (
        esFitxaPartida ? (
          renderActiveTab()
        ) : (
          <div className="dv2-entity-body">{renderArbreNivell0()}</div>
        )
      ) : (
        renderLlistaVersions()
      )}
    </div>
  );
}

export default Versions;
